import React, { useState, useEffect } from "react";
import { FilePdfOutlined, CheckCircleOutlined, CloseOutlined, MinusOutlined } from "@ant-design/icons";
import { Spin } from "antd";
import { fetchBilling, cancelSubscription, undoCancelSubscription } from "../services/api";
import "../../../styles/dashboardCardTokens.css";
import "./BillingSection.css";

const PRO_FEATURES = [
  "AI Market Insights & Demand Grading",
  "Private-Party Buy Signals",
  "Active + Sold Comparable Analysis",
  "Competitive Price Ranking & % to Market",
  "Dealer Lot Performance Tracking",
  "Unlimited VIN Analyses",
];

const CANCEL_FEATURES_REMOVED = [
  "Market Insights",
  "Buy Signals",
  "VIN Analyses",
  "Private Listings",
];

function formatBillingDate(isoString) {
  if (!isoString) return "—";
  const d = new Date(isoString);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function formatRenewDate(isoString) {
  if (!isoString) return "—";
  const d = new Date(isoString);
  return d.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
}

function formatExp(month, year) {
  if (month == null || year == null) return "—";
  const y = String(year).slice(-2);
  return `${String(month).padStart(2, "0")}/${y}`;
}

function formatAddress(billingDetails) {
  const a = billingDetails?.address;
  if (!a || typeof a !== "object") return null;
  const line1 = a.line1 || a.line_1;
  const city = a.city;
  const state = a.state;
  const postal = a.postal_code || a.postal;
  const country = a.country;
  const parts = [line1, [city, state, postal].filter(Boolean).join(", "), country].filter(Boolean);
  return parts.length ? parts : null;
}

/** Display status for Billing: normalize "paid" to "Paid". */
function formatBillingStatus(raw) {
  if (raw == null || String(raw).trim() === "") return "—";
  const s = String(raw).trim().toLowerCase();
  if (s === "paid") return "Paid";
  return String(raw).charAt(0).toUpperCase() + String(raw).slice(1).toLowerCase();
}

/** Normalize subscription from API: support plan_name, price, renew_date and common alternatives (e.g. Stripe). */
function normalizeSubscription(sub) {
  if (!sub || typeof sub !== "object") return {};
  const planName =
    sub.plan_name ??
    sub.plan_name_display ??
    sub.product?.name ??
    sub.nickname ??
    sub.plan?.nickname ??
    sub.items?.[0]?.price?.nickname ??
    sub.items?.[0]?.plan?.nickname ??
    (sub.plan_id || sub.items?.[0] ? "Hooptie Elite" : null);

  let price =
    sub.price ??
    sub.amount ??
    sub.unit_amount ??
    sub.items?.[0]?.price?.unit_amount ??
    sub.items?.[0]?.price?.amount ??
    sub.plan?.amount ??
    sub.default_price?.unit_amount ??
    sub.default_price?.amount;
  if (price != null) {
    price = Number(price);
    if (price > 1000) price = price / 100;
  }

  let renewDateIso =
    sub.renew_date ??
    sub.period_end ??
    sub.next_billing_date;
  const rawEnd = sub.current_period_end;
  if (renewDateIso == null && rawEnd != null) {
    const n = Number(rawEnd);
    if (n > 0) {
      renewDateIso = n < 1e10 ? new Date(n * 1000).toISOString() : new Date(n).toISOString();
    } else if (typeof rawEnd === "string") renewDateIso = rawEnd;
  }

  const creationDateRaw = sub.creation_date ?? sub.created ?? sub.start_date;
  let creationDateIso = creationDateRaw;
  if (creationDateRaw != null && typeof creationDateRaw === "number") {
    creationDateIso = creationDateRaw < 1e10
      ? new Date(creationDateRaw * 1000).toISOString()
      : new Date(creationDateRaw).toISOString();
  }

  return {
    ...sub,
    plan_name: planName ?? "Hooptie Elite",
    price: price != null ? price : sub.price,
    renew_date: renewDateIso ?? sub.renew_date,
    creation_date: creationDateIso ?? creationDateRaw ?? sub.creation_date,
  };
}

export default function BillingSection() {
  const [billing, setBilling] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [isCancelling, setIsCancelling] = useState(false);
  const [cancelError, setCancelError] = useState(null);
  const [isUndoing, setIsUndoing] = useState(false);
  const [undoError, setUndoError] = useState(null);
  const [undoSuccess, setUndoSuccess] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const timeout = setTimeout(() => {
      if (!cancelled) setLoading(false);
    }, 10000);

    fetchBilling()
      .then((data) => {
        if (!cancelled) setBilling(data);
      })
      .catch(() => {
        if (!cancelled) setBilling(null);
        setLoadError(true);
      })
      .finally(() => {
        if (!cancelled) {
          clearTimeout(timeout);
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, []);

  const closeCancelModal = () => {
    if (isCancelling) return;
    setCancelModalOpen(false);
    setCancelReason("");
    setCancelError(null);
  };

  const handleConfirmCancel = async () => {
    setIsCancelling(true);
    setCancelError(null);
    try {
      await cancelSubscription(cancelReason);
      setCancelModalOpen(false);
      setCancelReason("");
      setBilling(null);
      fetchBilling().then((data) => setBilling(data)).catch(() => {});
    } catch (err) {
      setCancelError(err?.response?.data?.detail ?? err?.message ?? "Failed to cancel. Please try again.");
    } finally {
      setIsCancelling(false);
    }
  };

  const handleUndoCancel = async () => {
    setIsUndoing(true);
    setUndoError(null);
    setUndoSuccess(false);
    try {
      await undoCancelSubscription();
      setBilling(null);
      const data = await fetchBilling();
      setBilling(data);
      setUndoSuccess(true);
      window.setTimeout(() => setUndoSuccess(false), 5000);
    } catch (err) {
      setUndoError(err?.response?.data?.detail ?? err?.message ?? "Failed to undo cancellation. Please try again.");
    } finally {
      setIsUndoing(false);
    }
  };

  const billingData = billing?.data ?? billing;
  const rawSubscription = billingData?.subscription;
  const sub = normalizeSubscription(rawSubscription ?? {});
  const planName = sub.plan_name ?? "—";
  const price = sub.price != null ? sub.price : null;
  const renewDateIso = sub.renew_date;
  const renewDateFormatted = formatRenewDate(renewDateIso);
  const status = sub.status ?? "—";

  const isCancelled =
    sub.cancelled === true ||
    sub.cancel_at_period_end === true ||
    (status && String(status).toLowerCase() === "cancelled") ||
    !!sub.cancelled_at;
  const cancelledDateIso = sub.cancelled_at ?? sub.cancel_at ?? renewDateIso;
  const cancelledDateFormatted = formatRenewDate(cancelledDateIso);

  const creationDateIso = sub.creation_date;
  const isWithinFreeTrialPeriod = (() => {
    if (!creationDateIso) return false;
    const created = new Date(creationDateIso).getTime();
    if (Number.isNaN(created)) return false;
    const sevenDaysLater = created + 7 * 24 * 60 * 60 * 1000;
    return Date.now() < sevenDaysLater;
  })();

  const pm = billingData?.default_payment_method;
  const cardBrand = pm?.brand ? String(pm.brand).toUpperCase() : null;
  const cardLast4 = pm?.last4 ?? null;
  const cardExp = pm?.exp ?? formatExp(pm?.exp_month, pm?.exp_year);
  const billingDetails = pm?.billing_details;
  const addressLines = formatAddress(billingDetails);
  const billingName = billingDetails?.name;
  const billingEmail = billingDetails?.email;

  const invoices = Array.isArray(billingData?.invoices) ? billingData.invoices : [];

  if (loading) {
    return (
      <div className="billing-section billing-section-loading">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="billing-section">
      {loadError && (
        <div className="billing-load-error">
          Could not load billing data. Showing placeholders. Check that the backend is running and <code>/api/billing/overview</code> returns subscription data.
        </div>
      )}
      <div className="billing-grid">
        <div className="account-card dashboard-card-base billing-plan-card billing-plan-card--featured">
          <h3 className="account-card-title dashboard-card-title billing-plan-title">
            {planName}
          </h3>
          <div className="billing-plan-price">
            {price != null ? `$${Number(price).toFixed(0)}` : "—"}{" "}
            <span className="billing-plan-period">/month</span>
          </div>
          <div className="billing-plan-renewal">
            {isCancelled ? `Cancelling on ${cancelledDateFormatted}` : `Renews ${renewDateFormatted}`}
          </div>
          <ul className="billing-plan-features billing-plan-features-cols">
            {PRO_FEATURES.map((label) => (
              <li key={label}>
                <CheckCircleOutlined className="billing-plan-check" />
                <span>{label}</span>
              </li>
            ))}
          </ul>
          <div className="billing-plan-actions">
            {undoSuccess && (
              <div className="billing-plan-actions-success">
                <CheckCircleOutlined /> Your membership has been reactivated. You'll continue to have access until the end of your billing period.
              </div>
            )}
            {undoError && (
              <p className="billing-plan-actions-error">{undoError}</p>
            )}
            <button
              type="button"
              className={isCancelled ? "billing-btn-outline billing-btn-undo" : "billing-btn-outline"}
              onClick={isCancelled ? handleUndoCancel : () => setCancelModalOpen(true)}
              disabled={isUndoing}
            >
              {isCancelled
                ? (isUndoing ? "Undoing…" : "Undo Cancellation")
                : "Cancel Membership"}
            </button>
          </div>
        </div>

      </div>

      <div className="account-card dashboard-card-base billing-history-card">
        <h3 className="account-card-title dashboard-card-title">Billing History</h3>
        <div className="billing-history-table-wrap">
          <table className="billing-history-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Invoice #</th>
                <th>Amount</th>
                <th>Status</th>
                <th>Invoice</th>
              </tr>
            </thead>
            <tbody>
              {invoices.length === 0 ? (
                <tr>
                  <td colSpan={5} className="billing-history-empty">
                    No invoices yet
                  </td>
                </tr>
              ) : (
                invoices.map((inv, i) => (
                  <tr key={inv.number || i}>
                    <td>{formatBillingDate(inv.date)}</td>
                    <td>{inv.number ?? "—"}</td>
                    <td>{inv.amount != null ? `$${Number(inv.amount).toFixed(2)}` : "—"}</td>
                    <td>
                      <span className={inv.status != null && String(inv.status).toLowerCase() === "paid" ? "billing-status-paid" : ""}>
                        {inv.status != null && String(inv.status).toLowerCase() === "paid" && <CheckCircleOutlined />}{" "}
                        {formatBillingStatus(inv.status)}
                      </span>
                    </td>
                    <td>
                      {inv.invoice_pdf ? (
                        <a
                          href={inv.invoice_pdf}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="billing-download-btn"
                        >
                          <FilePdfOutlined />
                          Download PDF
                        </a>
                      ) : (
                        <span className="billing-download-unavailable">—</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {cancelModalOpen && (
        <div className="billing-cancel-modal-overlay" onClick={closeCancelModal} role="dialog" aria-modal="true" aria-labelledby="cancel-modal-title">
          <div className="billing-cancel-modal" onClick={(e) => e.stopPropagation()}>
            <button type="button" className="billing-cancel-modal-close" onClick={closeCancelModal} aria-label="Close">
              <CloseOutlined />
            </button>
            <h2 id="cancel-modal-title" className="billing-cancel-modal-title">
              Are you sure you want to cancel your {planName}?
            </h2>
            <p className="billing-cancel-modal-intro">Cancelling will immediately remove access to:</p>
            <ul className="billing-cancel-modal-list">
              {CANCEL_FEATURES_REMOVED.map((label) => (
                <li key={label}>
                  <span className="billing-cancel-modal-bullet"><MinusOutlined /></span>
                  <span>{label}</span>
                </li>
              ))}
            </ul>
            <p className="billing-cancel-modal-note">
              {isWithinFreeTrialPeriod
                ? "Your account will be cancelled today. You're within your 7-day free trial—you won't be charged."
                : `After ${renewDateFormatted}, any unused features will expire and you won't be charged again.`}
            </p>
            <div className="billing-cancel-modal-reason">
              <label htmlFor="billing-cancel-reason" className="billing-cancel-modal-reason-label">
                Why are you cancelling?
              </label>
              <textarea
                id="billing-cancel-reason"
                className="billing-cancel-modal-reason-input"
                placeholder="Please tell us why you're cancelling..."
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                rows={3}
              />
            </div>
            {cancelError && (
              <p className="billing-cancel-modal-error">{cancelError}</p>
            )}
            <div className="billing-cancel-modal-actions">
              <button type="button" className="billing-cancel-btn-keep" onClick={closeCancelModal} disabled={isCancelling}>Keep {planName}</button>
              <button
                type="button"
                className="billing-cancel-btn-confirm"
                onClick={handleConfirmCancel}
                disabled={isCancelling || !cancelReason.trim()}
              >
                {isCancelling ? "Cancelling…" : "Cancel Membership"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
